import React from 'react';
import get from 'lodash/get';
import classNames from 'classnames';
import * as PropTypes from 'prop-types';

import Helpers from '@utils/Helpers';
import Avatar from '@components/shared/Avatar';
import Title from '@components/shared/Title';
import Link from '@components/shared/Link';
import Chips from '@components/shared/Chips';
import Button from '@components/shared/Button';
import ActionIcon from '@components/shared/ActionIcon';
import { useCureLikes } from '@hooks/useCureLikes';
import { apiUrls } from '@constants/api';
import routes from '@constants/routes';

const SuccessStory = ({ story, author, empty, disabled, className }) => {
  const userLink = Helpers.getUserLink(get(author, 'id'));

  const likeUrls = {
    add: apiUrls.combinations.like(story.id),
    remove: apiUrls.combinations.removeLike(story.id)
  };

  const detailsLink = disabled
    ? {
        href: '#'
      }
    : {
        href: routes.combinations.details.pattern,
        as: routes.combinations.details.path(story.disease_condition, story.id)
      };

  const storyItems = [
    {
      title: 'Supplement Mix',
      array: story.supplement_mix,
      visible: 2,
      getChipLabel: chip => chip.name
    },
    {
      title: 'Product Suggestions',
      array: story.products,
      visible: 2,
      getChipLabel: chip => chip.name
    },
    {
      title: 'Medicine',
      array: story.medicine,
      visible: 2,
      getChipLabel: text => text
    },
    {
      title: 'Diet',
      array: story.diets,
      visible: 2,
      getChipLabel: chip => chip.name
    }
  ];

  const { isLiked, likesCount, handleToggleLike } = useCureLikes(
    story,
    likeUrls.add,
    likeUrls.remove
  );

  return (
    <div
      className={classNames(
        'success-story',
        {
          'success-story--empty': empty,
          'success-story--disabled': disabled
        },
        className
      )}
    >
      <div className="success-story__content">
        {(!empty && (
          <>
            {author && (
              <Link {...userLink} className="success-story__avatar">
                <Avatar src={get(author, 'profile_avatar')} />
              </Link>
            )}

            <div className="success-story__like">
              <ActionIcon
                type="like"
                isActive={isLiked}
                onClick={handleToggleLike}
                className="success-story__toggle"
              />
              {!disabled && (
                <span className="success-story__count">
                  {Helpers.formatLikesAmount(likesCount)}
                </span>
              )}
            </div>

            <div className="success-story__header">
              <Link {...userLink} className="success-story__title">
                {get(author, 'username')}
              </Link>
              {story.life_tips.trim() && (
                <Link className="success-story__link-wrapper" {...detailsLink}>
                  <div className="success-story__text">
                    “{story.life_tips.trim()}”
                  </div>
                </Link>
              )}
            </div>

            <div className="success-story__body">
              <Link className="success-story__link-wrapper" {...detailsLink}>
                {storyItems.map(item => {
                  if (item.render) {
                    return item.render();
                  }

                  return (
                    Helpers.arrayHasItems(item.array) && (
                      <Chips
                        key={item.title}
                        title={item.title}
                        className="success-story__chips"
                        array={item.array}
                        visible={item.visible}
                        getChipLabel={item.getChipLabel}
                      />
                    )
                  );
                })}
              </Link>
            </div>
            <div className="success-story__footer">
              <Link
                {...detailsLink}
                icon="chevron-right"
                className="success-story__link"
              >
                See details
              </Link>
            </div>
          </>
        )) || (
          <>
            <div className="success-story__empty-media">
              <img
                alt="empty-image"
                src={get(empty, 'src', '/images/avatars-group.svg')}
                className="success-story__empty-img"
              />
            </div>
            <Title
              size="xs"
              bold={true}
              heading="h3"
              className="success-story__empty-title"
            >
              {get(empty, 'title', 'We have a lot more!')}
            </Title>
            <Button
              color="yellow"
              className="success-story__empty-btn"
              href={get(empty, 'button.href', null)}
              onClick={get(empty, 'button.onClick', null)}
            >
              {get(empty, 'button.text', 'See all')}
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

SuccessStory.defaultProps = {
  story: {},
  author: {},
  disabled: false
};

SuccessStory.propTypes = {
  story: PropTypes.object,
  author: PropTypes.object,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  empty: PropTypes.oneOfType([PropTypes.bool, PropTypes.object])
};

export default SuccessStory;
